import { DatePipe } from '@angular/common';
import { Component, computed, effect, inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { AbstractControl, FormBuilder, ReactiveFormsModule, ValidatorFn, Validators } from '@angular/forms';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { ENVIRONMENT } from '../../../../../../environments/environment.interface';
import {
	Assignment,
	AssignmentWithRelations,
	AssingmentImpedimentReportMinMaxDueDate,
	UpdateAssignmentBody,
} from '../../../../../api/app-types/assignment';
import { Client } from '../../../../../api/app-types/client.app';
import { DisabilityKind } from '../../../../../api/app-types/disability-type.app';
import { DocumentFile, DocumentFileSubjectType } from '../../../../../api/app-types/document.app';
import { ASSIGNMENT_API } from '../../../../../api/services/assignment.service';
import { DB_PHASES } from '../../../../../api/app-types/phase.app';
import { CUSTOM_DATE_FORMATS } from '../../../../../core/adapters/custom-date-format';
import { LvrRedirectNav } from '../../../../../core/classes/redirect-nav.class';
import { LvrDefaultActions } from '../../../../../core/components/default-actions/default.actions';
import { LvrField } from '../../../../../core/components/field/field';
import { LvrSection } from '../../../../../core/components/section/section';
import { LvrSectionForm } from '../../../../../core/components/section/section-form';
import { LvrInputDirective } from '../../../../../core/directives/lvr-input.directive';
import { STATE } from '../../../../../core/services/state.service';
import { LvrAppendAsteriskPipe } from '../../../../../shared/pipes/append-asterisk.pipe';
import { DocumentUploadControl } from '../../../../../shared/document-upload/document-upload';
import {
	allowedFileTypes,
	maxFiles,
	maxFileSize,
} from '../../../../../shared/document-upload/document-upload-validators';
import { matDateProviders } from '../../../../../shared/helpers/date.func';
import {
	normalizeToDayStart,
	resolveMaxStartDate,
	resolveMinStartDate,
	resolveP3StartDate,
	toDayKey,
} from '../../../../../shared/helpers/additional-assignment-date.helper';
import { latestContactDate } from '../../../../../shared/helpers/assignment.func';
import { NOTIFICATION_SERVICE } from '../../../../../shared/notification/notification';
import {
	createErrorNotification,
	createSuccessNotification,
} from '../../../../../shared/notification/notification.factory';
import { LvrPaddingDirective } from '../../../../../shared/padding/padding.directive';
import { PaginatedResponse } from '../../../../../shared/pagination/paginated-response';
import { ClientResolved } from '../../../../../shared/routing/resolvers/client.resolver';
import { LvrAutoSaveDirective } from '../../../../../shared/storage/auto-save.directive';
import { FORM_KEYS } from '../../../../../shared/storage/form-keys.type';
import { FormPersistenceService } from '../../../../../shared/storage/form-persistence.service';
import { LvrRestoreActions } from '../../../../../shared/storage/restore-actions';
import { ImmediateErrorStateMatcher } from '../../../../../shared/validators/immediate-error-state-matcher';
import { maxDateValidator, minDateValidator } from '../../../../../shared/validators/max-date-validator';
import { hasPermission } from '../../../../../shared/permissions/permission.func';
import { SessionManagerService } from '../../../../../shared/storage/session-manager.service';

@Component({
	selector: 'lvr-additional-assignment-edit',
	templateUrl: './additional-assignment-edit.html',
	styleUrl: './additional-assignment-edit.scss',
	imports: [
		TranslocoPipe,
		DatePipe,
		ReactiveFormsModule,
		LvrPaddingDirective,
		LvrField,
		MatFormFieldModule,
		LvrInputDirective,
		MatDatepickerModule,
		MatInput,
		MatSelectModule,
		DocumentUploadControl,
		LvrAppendAsteriskPipe,
		LvrRestoreActions,
		LvrDefaultActions,
		LvrSection,
		LvrSectionForm,
		LvrAutoSaveDirective,
	],
	providers: [matDateProviders],
})
export class AdditionalAssignmentEdit extends LvrRedirectNav {
	private readonly assignmentApi = inject(ASSIGNMENT_API);
	private readonly notificationService = inject(NOTIFICATION_SERVICE);
	private readonly transloco = inject(TranslocoService);
	private readonly draftService = inject(FormPersistenceService);
	private readonly environment = inject(ENVIRONMENT);
	private readonly session = inject(SessionManagerService);
	private readonly state = inject(STATE);
	private readonly fb = inject(FormBuilder);
	private readonly clientResolved = this.resolveDataAsSignal<ClientResolved | undefined>('client');
	public readonly client = computed<Client>(() => this.clientResolved()?.value as Client);
	public readonly clientId = this.resolveDataAsSignal<number | string>('clientId');
	public readonly assignment = this.resolveDataAsSignal<AssignmentWithRelations | undefined>('assignment');
	private readonly mainAssignment = this.resolveDataAsSignal<Assignment | undefined>('activeAssignment');
	public readonly assignmentId = computed(() => this.assignment()?.id ?? -1);
	private readonly disabilityKindsPage = this.resolveDataAsSignal<PaginatedResponse<DisabilityKind>>('disabilityKinds');
	private readonly impedimentDateRange = this.resolveDataAsSignal<AssingmentImpedimentReportMinMaxDueDate>('dateRange');
	public readonly disabilityKinds = computed(() => this.disabilityKindsPage()?.entities ?? []);
	public readonly dateDisplayFormat: string = CUSTOM_DATE_FORMATS.display.dateInput;
	public readonly dateFormatHint = this.environment.visualDateFormat;
	public readonly maxFileCount = this.environment.contactSettings.maxFiles;
	public readonly maxFileSize = this.environment.fileSettings.maxFileSize;
	public readonly subjectType = DocumentFileSubjectType.ASSIGNMENT;
	public readonly canEditAssignmentsPermission = hasPermission(this.session.permissions, 'Assignments.Edit');
	public readonly canManageAssignmentDocuments = hasPermission(this.session.permissions, 'Documents.Assignment.Edit');
	public readonly matcher = new ImmediateErrorStateMatcher();
	public readonly isLoading = signal(false);

	public readonly minStartDate = computed(() => resolveMinStartDate(this.environment, this.p3StartDate()));
	public readonly maxStartDate = computed(() => resolveMaxStartDate());
	private readonly p3StartDate = computed(() => resolveP3StartDate(this.mainAssignment()));
	public readonly isDisabilityMandatory = computed(() => this.assignment()?.phaseId !== DB_PHASES.P0);
	public readonly isPhaseP3 = computed(() => this.assignment()?.phaseId === DB_PHASES.P3);
	private readonly latestAdditionalContactDate = computed(() => {
		const latest = latestContactDate(this.assignment());
		return latest ? normalizeToDayStart(latest) : undefined;
	});
	public readonly maxCancellationDate = computed(() => new Date());
	public readonly minCancellationDate = computed(() => {
		const assignment = this.assignment();
		const latestContact = this.latestAdditionalContactDate();
		const latestContactDay = latestContact ? normalizeToDayStart(new Date(latestContact)) : null;

		const phaseStart = assignment?.careStartDate ?? assignment?.phaseStart;
		const phaseStartDay = phaseStart ? normalizeToDayStart(new Date(phaseStart)) : null;

		const sixWeeksAgo = normalizeToDayStart(new Date());
		sixWeeksAgo.setDate(sixWeeksAgo.getDate() - 42);

		const possibleDates = [sixWeeksAgo, phaseStartDay, latestContactDay].filter((date): date is Date => !!date);

		return new Date(Math.max(...possibleDates.map((date) => date.getTime())));
	});

	public readonly draftId = computed(() => `${FORM_KEYS.EDIT_ADDITIONAL}.${this.assignmentId()}`);

	public readonly minImpedimentReportDueDate = computed(() => {
		const minDate = this.impedimentDateRange()?.minStartDate;
		minDate?.setHours(0, 0, 0, 0);
		return minDate;
	});
	public readonly maxImpedimentReportDueDate = computed(() => this.impedimentDateRange()?.maxEndDate);

	public readonly form = this.fb.group({
		startOfCare: this.formControlWithDate(this.assignment()?.careStartDate ?? null),
		careRelatedHandicapId: this.fb.control<number | null>(this.assignment()?.careRelatedHandicapId ?? null),
		cancellationDate: this.fb.control<Date | null>(this.assignment()?.cancellationDate ?? null),
		documents: this.fb.nonNullable.control<DocumentFile[]>(
			[],
			[
				maxFiles(this.maxFileCount),
				maxFileSize(this.maxFileSize),
				allowedFileTypes(this.environment.fileSettings.allowedMimeTypes),
			],
		),
		impedimentReportDueDate: this.fb.control(this.assignment()?.impedimentReportDueDate),
	});

	private readonly initialState = signal<{
		startOfCare?: string;
		cancellationDate?: string;
		careRelatedHandicapId: number | null;
		documentIds: string[];
		impedimentReportDueDateChanges?: string;
	} | null>(null);
	private readonly formChanges = toSignal(this.form.valueChanges, { initialValue: this.form.value });
	private readonly startOfCareChanges = toSignal(this.form.get('startOfCare')!.valueChanges, {
		initialValue: this.form.get('startOfCare')!.value,
	});
	private readonly careRelatedHandicapChanges = toSignal(this.form.get('careRelatedHandicapId')!.valueChanges, {
		initialValue: this.form.get('careRelatedHandicapId')!.value,
	});
	private readonly cancellationDateChanges = toSignal(this.form.get('cancellationDate')!.valueChanges, {
		initialValue: this.form.get('cancellationDate')!.value,
	});
	private readonly documentsChanges = toSignal(this.form.get('documents')!.valueChanges, {
		initialValue: this.form.get('documents')!.value,
	});
	private readonly impedimentReportDueDateChanges = toSignal(this.form.get('impedimentReportDueDate')!.valueChanges, {
		initialValue: this.form.get('impedimentReportDueDate')!.value,
	});

	public readonly isImpedimentReportDueDateDirty = computed(() => {
		this.impedimentReportDueDateChanges();

		return !!this.form.get('impedimentReportDueDate')?.dirty;
	});

	private validateOnlyWhenDirty(validators: ValidatorFn[]): ValidatorFn {
		return (control: AbstractControl) => {
			if (!control.dirty) {
				return null;
			}

			return Validators.compose(validators)?.(control) ?? null;
		};
	}

	public constructor() {
		super();
		effect(() => {
			const assignment = this.assignment();
			if (!assignment) return;

			const docs = assignment.documents ?? [];
			this.form.patchValue(
				{
					startOfCare: assignment.careStartDate ?? null,
					careRelatedHandicapId: assignment.careRelatedHandicapId ?? null,
					cancellationDate: assignment.cancellationDate ?? null,
					impedimentReportDueDate: assignment.impedimentReportDueDate ?? null,
				},
				{ emitEvent: false },
			);
			this.form.get('documents')?.setValue(docs, { emitEvent: true });
			this.form.markAsPristine();
			this.initialState.set({
				startOfCare: this.toDayKey(assignment.careStartDate ?? null),
				cancellationDate: this.toDayKey(assignment.cancellationDate ?? null),
				careRelatedHandicapId: assignment.careRelatedHandicapId ?? null,
				documentIds: docs.map((doc) => doc.id),
				impedimentReportDueDateChanges: this.toDayKey(assignment.impedimentReportDueDate ?? null),
			});
		});

		effect(() => {
			const min = this.minStartDate();
			const max = this.maxStartDate();
			const control = this.form.get('startOfCare');
			if (!control) return;
			control.setValidators([Validators.required, minDateValidator(min), maxDateValidator(max)]);
			if (this.isPhaseP3()) {
				control.disable({ emitEvent: false });
			} else {
				control.enable({ emitEvent: false });
			}
			control.updateValueAndValidity({ emitEvent: false });
		});

		effect(() => {
			const control = this.form.get('careRelatedHandicapId');
			if (!control) return;
			const validators = this.isDisabilityMandatory() ? [Validators.required] : [];
			control.setValidators(validators);
			control.updateValueAndValidity({ emitEvent: false });
		});

		effect(() => {
			const control = this.form.get('cancellationDate');
			if (!control) return;
			const min = this.minCancellationDate();
			const max = this.maxCancellationDate();
			const validators = [minDateValidator(min), maxDateValidator(max)];
			if (this.isPhaseP3()) validators.push(Validators.required);
			control.setValidators(validators);
			control.updateValueAndValidity({ emitEvent: false });
		});

		// Validate legacy due dates only after the user changes them
		effect(() => {
			this.impedimentReportDueDateChanges();

			const control = this.form.get('impedimentReportDueDate');
			if (!control) return;

			control.setValidators([
				this.validateOnlyWhenDirty([
					minDateValidator(this.minImpedimentReportDueDate()),
					maxDateValidator(this.maxImpedimentReportDueDate()),
				]),
			]);

			control.updateValueAndValidity({ emitEvent: false });
		});
	}

	private formControlWithDate(initial: Date | null) {
		return this.fb.control<Date | null>(initial, [Validators.required]);
	}

	private toDayKey(date?: Date | null) {
		return toDayKey(date);
	}

	private areArraysEqual(a: string[], b: string[]) {
		if (a.length !== b.length) return false;
		const sortedA = [...a].sort();
		const sortedB = [...b].sort();
		return sortedA.every((value, index) => value === sortedB[index]);
	}

	public hasError(control: AbstractControl | null, ...errors: string[]) {
		if (!control) return false;
		return errors.some(
			(error) => control.hasError(error) && (control.dirty || !!control.value?.length || control.touched),
		);
	}

	public hasFileError(control: AbstractControl | null, ...errors: string[]) {
		if (!control) return '';

		const error = errors.find(
			(err) => control.hasError(err) && (control.dirty || !!control.value?.length || control.touched),
		);
		return error ?? '';
	}

	public getDisabilityKindName(id: number) {
		if (!id) return '';
		return this.disabilityKinds().find((b) => b.id === id)?.disabilityType?.remark ?? '';
	}

	public getDisabilityKindDifferentiation(id: number) {
		if (!id) return '';
		return this.disabilityKinds().find((b) => b.id === id)?.categoryDiagnosis?.description ?? '-';
	}

	public cancel() {
		this.draftService.deleteDraft('additional', this.draftId());
		this.navigateAfterFinished(['../', 'additional'], { relativeTo: this.route });
	}

	public async save() {
		if (this.isLoading()) return;

		if (this.form.invalid) {
			this.form.markAllAsTouched();
			return;
		}

		const { startOfCare, careRelatedHandicapId, documents, cancellationDate, impedimentReportDueDate } =
			this.form.getRawValue();
		const documentIds = documents.map((doc) => doc.id);

		const payload: UpdateAssignmentBody & { documentIds?: string[] } = {
			careStartDate: startOfCare ?? undefined,
			careRelatedHandicapId: careRelatedHandicapId ?? undefined,
			cancellationDate: cancellationDate ?? undefined,
			documentIds: Array.from(new Set(documentIds)),
			impedimentReportDueDate: impedimentReportDueDate ?? undefined,
		};

		this.isLoading.set(true);
		try {
			await this.assignmentApi.update(this.clientId()!, this.assignmentId(), payload);
			await this.state.updateAssignments();
			this.draftService.deleteDraft('additional', this.draftId());
			await this.navigateAfterFinished(['../', 'additional'], { relativeTo: this.route });
			this.notificationService.addNotification(
				createSuccessNotification(
					this.transloco.translate('base.notification_message.additional_assignment_edit_success'),
				),
			);
		} catch (error) {
			console.error(error);
			this.notificationService.addNotification(
				createErrorNotification(this.transloco.translate('base.notification_message.additional_assignment_edit_error')),
			);
		} finally {
			this.isLoading.set(false);
		}
	}
}
